import { useTheme } from 'foundations-theming/utils';
import { useEffect, useRef, useState } from 'react';
import styled from 'foundations-theming/utils';
import { AISkeleton } from './AISkeleton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PADDING = 8;
const LINE_HEIGHT = 24;
const StyledDiv = styled.div.withConfig({
  displayName: "AILoadingShimmer__StyledDiv"
})(["position:relative;height:", ";width:100%;flex:1;overflow:hidden;.ai-shimmer-skeleton{opacity:", ";position:absolute;margin-top:", ";margin-bottom:", ";margin-left:", "px;margin-right:", "px;top:", "px;bottom:0;left:", "px;right:", "px;z-index:", ";transition:opacity 0.3s;pointer-events:none;user-select:none;}.ai-shimmer-hide-content{opacity:", ";position:absolute;background-color:", " !important;top:", "px;bottom:", "px;left:", "px;right:", "px;z-index:", ";}"], ({
  height,
  isLoading
}) => isLoading ? `${height}px` : 'auto', ({
  isLoading
}) => isLoading ? 100 : 0, ({
  theme
}) => theme.space['0'], ({
  theme
}) => theme.space['0'], ({
  marginLeft
}) => marginLeft || 0, ({
  marginRight
}) => marginRight || 0, ({
  paddingTop = 0,
  marginTop = 0
}) => paddingTop + marginTop, ({
  paddingLeft
}) => paddingLeft || 0, ({
  paddingRight
}) => paddingRight || 0, ({
  zIndex
}) => zIndex || 1, ({
  isLoading
}) => isLoading ? 100 : 0, ({
  backgroundColor
}) => backgroundColor, ({
  marginTop
}) => marginTop || 0, ({
  marginBottom
}) => marginBottom || 0, ({
  marginLeft
}) => marginLeft || 0, ({
  marginRight
}) => marginRight || 0, ({
  zIndex
}) => zIndex || 1);
const calculateHeightFromNumberOfLines = numberOfLines => {
  return numberOfLines * LINE_HEIGHT;
};
const calculateNumberOfLines = height => {
  const numberOfLines = height / LINE_HEIGHT;
  return Math.max(Math.floor(numberOfLines), 1);
};
export const AIShimmer = ({
  isLoading = false,
  children,
  backgroundColor,
  // In case you wanna override it!
  minHeight = 0,
  paddingRight,
  paddingLeft,
  paddingBottom = 0,
  paddingTop = 0,
  fadeOutTimer = 0,
  marginTop = 0,
  marginBottom = 0,
  marginLeft = 0,
  marginRight = 0,
  zIndex = 1
}) => {
  const trellisTheme = useTheme();
  const [isLoadingState, setIsLoadingState] = useState(isLoading);
  const [contentHeight, setContentHeight] = useState(0);
  const childrenHeightRef = useRef(null);
  const oneSkeletonHeight = calculateHeightFromNumberOfLines(1);
  const minHeightOr1Skeleton = Math.max(minHeight, oneSkeletonHeight);
  const minNumberOfLines = calculateNumberOfLines(minHeightOr1Skeleton);
  useEffect(() => {
    // Update content height after initial render and when children change
    if (childrenHeightRef.current) {
      setContentHeight(childrenHeightRef.current.clientHeight);
    }
  }, [children]);
  if (!backgroundColor) {
    backgroundColor = trellisTheme.color['fill-field-default'];
  }
  const heightWithoutMarginOrPadding = Math.max(contentHeight || 0, minHeightOr1Skeleton);

  // Don't include padding top - or you'll get too many lines!
  const calculatedNumberOfLines = calculateNumberOfLines(heightWithoutMarginOrPadding + paddingBottom - paddingTop - marginBottom - marginTop);
  const height = heightWithoutMarginOrPadding + paddingBottom;
  const numberOfSkeletons = Math.max(calculatedNumberOfLines, minNumberOfLines);
  useEffect(() => {
    if (fadeOutTimer > 0) {
      setIsLoadingState(true);
      setTimeout(() => {
        setIsLoadingState(false);
      }, fadeOutTimer);
    }
  }, [fadeOutTimer]);
  useEffect(() => {
    setIsLoadingState(isLoading);
  }, [isLoading]);
  return /*#__PURE__*/_jsxs(StyledDiv, {
    isLoading: isLoadingState,
    numberOfLines: numberOfSkeletons,
    backgroundColor: backgroundColor,
    paddingRight: paddingRight,
    paddingLeft: paddingLeft,
    paddingTop: paddingTop,
    height: height,
    marginTop: marginTop,
    marginBottom: marginBottom,
    marginLeft: marginLeft,
    marginRight: marginRight,
    zIndex: zIndex,
    children: [isLoadingState && /*#__PURE__*/_jsx("div", {
      className: "ai-shimmer-hide-content"
    }), /*#__PURE__*/_jsx("div", {
      className: "ai-shimmer-skeleton",
      children: Array.from({
        length: numberOfSkeletons
      }).map((_, index) => {
        const showHalfWidth = index === numberOfSkeletons - 1 && numberOfSkeletons > 1;
        return /*#__PURE__*/_jsx(AISkeleton, {
          height: LINE_HEIGHT - PADDING,
          width: showHalfWidth ? '50%' : undefined
        }, index);
      })
    }), /*#__PURE__*/_jsx("div", {
      ref: childrenHeightRef,
      children: children
    })]
  });
};
AIShimmer.displayName = 'AIShimmer';